import { useQueryClient } from '@tanstack/react-query';
import useMutationApi from '../useMutationApi';
import { RebateAgreementDTO } from './types';

interface ReprocessAgreementRequest {
  supplierId: string;
  model?: string;
}

interface ReprocessAgreementResponse {
  id: string;
  supplierId: string;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Hook to reprocess an existing draft rebate agreement with a new supplier
 * - Fetches the existing document URLs from the agreement
 * - Runs AI extraction with the new supplier context
 * - Replaces the agreement with new supplier and extracted rules
 */
const useReprocessAgreementWithSupplier = (agreementId: string) => {
  const queryClient = useQueryClient();

  return useMutationApi<ReprocessAgreementRequest, ReprocessAgreementResponse>({
    endpoint: `rebates/agreements/${agreementId}/reprocess-with-supplier`,
    method: 'PUT',
    options: {
      onSuccess: () => {
        // Invalidate rebate agreements queries to refresh the data
        queryClient.invalidateQueries({ queryKey: ['rebateAgreements'] });
        queryClient.invalidateQueries({
          queryKey: ['rebateAgreement', agreementId],
        });
      },
    },
    successMessage: 'Agreement successfully reprocessed with new supplier',
  });
};

export default useReprocessAgreementWithSupplier;
